import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, ChevronDown, ChevronUp, ListFilter, Search, Layers, ArrowUpDown } from 'lucide-react';
import { Checkbox } from '@clay-run/glaze/checkbox';
import { useZebraStripes } from './zebraStripes';
import { MAX_GROUP_LEVELS } from '@shared/flow/view';
import { ResponsivePopover } from './ResponsivePopover';
import { FilterPopover } from './filters/FilterPopover';
import { FilterDraftBox } from './filters/FilterDraftBox';
import { FilterDraftProvider } from './filters/FilterDraftContext';
import { ColumnsPopover, type ColumnsPopoverColumn } from './columns/ColumnsPopover';
import { ViewSwitcher } from './views/ViewSwitcher';
import { ViewDirtyBox } from './views/ViewDirtyBox';
import type { FlowColumnDef, FlowSortOption, FlowTableProps } from './types';
import { groupableColumnsOf } from './groupableColumns';
import { fieldExamples } from '@shared/flow/field-catalog-order';
import { TrackerChip } from './tracker/TrackerChip';
import { TrackerStatusSchema, type TrackerStatus } from '@shared/flow/tracker';
import { reorderColumns, type ColumnDragSide } from './columnDrag';
import { isShiftFromCheckboxEvent } from './table-core/selection';
import { useRowSelectionGestures } from './table-core/useRowSelectionGestures';
import { AUTOFILL_IGNORE_PROPS } from '../../lib/autofill';
import { matchesFieldQuery } from './table-core/field-sections';
import { defaultDirFor, isSortableColumn, sortTriggerLabel } from './table-core/sorting';
import { GROUP_PATH_SEP, groupValueOf, moveLevel, resolveGroupCollapsed } from './table-core/grouping';
import { PINNED_COLUMN_KEY } from './table-core/columns';
import { snapUnitZoom } from './table-core/zoom';
import { formatCellValue } from './table-core/format';
import { activeFilterCount as countActiveFilters } from './table-core/filter-chips';

// `moveLevel` moved to `table-core/grouping.ts` (plan 2026-09-27-007 U1);
// re-exported so FlowTable's public surface is unchanged.
export { moveLevel };

/**
 * Generic Flow table — a dense, SFDC-report-style table (prototype-anchored):
 * clickable headers sort, the filter builder filters, single-field group-by
 * renders subheaders. Entirely server-driven: every control change re-queries
 * page 1; "Load more" follows the keyset cursor. Rows expand in place into a
 * config-supplied detail (the action-view-style dropdown).
 *
 * Dark mode: semantic tokens are static (no .dark overrides), so this file
 * uses explicit `dark:` variants per the established mini-app convention
 * (nightshade surface scale — see action-view).
 */

// `FlowTableProps` moved to `types.ts` (thermo-nuclear review T1) — the V2
// `LedgerTable`/model-hook contract shouldn't live in the file it replaces.
// Imported above alongside its sibling types.

/**
 * Accent-dot color per nesting level (PR1 U4/D7/D8) — existing semantic
 * tokens only (`terra/no-raw-color-literals`), same three already used as
 * ContactTimeline's own dot-accent palette (`contact/ContactTimeline.tsx`).
 * A fourth level never renders (`MAX_GROUP_LEVELS` caps the picker at 3), so
 * this never needs a fourth entry.
 */
const GROUP_LEVEL_ACCENT_CLASSES = ['bg-content-action', 'bg-content-success', 'bg-content-warning'];

/**
 * Group-header `<td>` classes per `FlowTableConfig.groupHeaderStyle` (QA
 * batch 1 D-A). COMPACT is the pre-existing string, verbatim — every mount
 * that doesn't opt in renders byte-identical markup. SECTION swaps only the
 * height (`py-1` → `h-12`, 48px) and the type size (`text-[11px]` →
 * `text-[12px]`); every colour token is shared.
 */
const GROUP_HEADER_COMPACT_CLASS = 'pr-2.5 py-1 bg-bg-secondary dark:bg-nightshade-800 border-b border-secondary dark:border-nightshade-700 text-[11px] font-semibold text-content-secondary dark:text-nightshade-400 cursor-pointer select-none';
const GROUP_HEADER_SECTION_CLASS = 'pr-2.5 h-12 bg-bg-secondary dark:bg-nightshade-800 border-b border-secondary dark:border-nightshade-700 text-[12px] font-semibold text-content-secondary dark:text-nightshade-400 cursor-pointer select-none';

/**
 * Floating-card grid (`cardMode` only). Card mode renders the table
 * `border-separate` with zero spacing — collapsed borders ignore
 * `border-radius`, which painted a square outline around every rounded card
 * corner. In separate mode each cell paints its OWN borders, so the grid is
 * re-expressed as one ownership rule shared by the column header, group
 * header rows and data rows alike (which is what keeps the vertical dividers
 * continuous through group headers):
 *  - every cell owns its bottom line;
 *  - the first cell owns the card's left edge;
 *  - a cell owns its right line, EXCEPT the chevron column (unlined, same as
 *    compact mode) and the last data column when a sticky action column
 *    follows — the action cell owns that seam as its `border-l` (D33a: it
 *    needs its own edge while horizontally scrolled), so drawing both would
 *    double the line;
 *  - the last cell always owns the card's right edge.
 * `top` adds the card's top line (every cell); `roundTop`/`roundBottom`
 * round only the two outer cells.
 */
type CardColumnKind = 'select' | 'chevron' | 'tracker' | 'data' | 'action';
const CARD_BORDER_COLOR = 'border-secondary dark:border-nightshade-700';
function cardCellEdges(
  kinds: readonly CardColumnKind[],
  idx: number,
  opts: { top?: boolean; bottom?: boolean; roundTop?: boolean; roundBottom?: boolean } = {},
): string {
  const kind = kinds[idx];
  const isFirst = idx === 0;
  const isLast = idx === kinds.length - 1;
  const parts: string[] = [CARD_BORDER_COLOR];
  if (opts.bottom !== false) parts.push('border-b');
  if (opts.top) parts.push('border-t');
  if (isFirst || kind === 'action') parts.push('border-l');
  const ownsRight = isLast
    || kind === 'select'
    || kind === 'tracker'
    || (kind === 'data' && kinds[idx + 1] !== 'action');
  if (ownsRight) parts.push('border-r');
  if (opts.roundTop && isFirst) parts.push('rounded-tl-lg');
  if (opts.roundTop && isLast) parts.push('rounded-tr-lg');
  if (opts.roundBottom && isFirst) parts.push('rounded-bl-lg');
  if (opts.roundBottom && isLast) parts.push('rounded-br-lg');
  return parts.join(' ');
}

/**
 * Stable "ungrouped" fallback — a fresh `[]` literal on every render would
 * be a NEW reference each time, which defeats `groupCounts`'s `useMemo` (its
 * dependency array would never see `groupLevels` as unchanged, per
 * `react-hooks/exhaustive-deps`). One module-scope constant means the
 * ungrouped case is referentially stable across renders, exactly like the
 * caller's own array reference is when `groupBy` is actually set.
 */
const EMPTY_GROUP_LEVELS: string[] = [];

/**
 * Floor for a drag-resize (U10). Small enough to be a genuine "collapse this
 * to a sliver" gesture, large enough that the 6px handle stays grabbable —
 * a column that could reach 0 would be unrecoverable without the Columns
 * popover.
 */
const MIN_COLUMN_WIDTH_PX = 60;

/**
 * Shared toolbar-button styling for the three popover triggers. EXPORTED
 * (U13/A6; since A11 the Plays list is itself a `FlowTable` mount, so both
 * sections share this chrome by construction) — Spencer's QA-round-1 rule is that
 * the two stacked sections look like one table twice, not two different
 * tables. Re-declaring these strings there is how they drift.
 */
export const TOOLBAR_BUTTON_IDLE =
  'border-secondary dark:border-nightshade-700 text-content-secondary dark:text-nightshade-400 bg-white dark:bg-nightshade-800 hover:bg-bg-secondary dark:hover:bg-nightshade-700';
export const TOOLBAR_BUTTON_ACTIVE =
  'border-content-action text-content-action bg-bg-blue-light dark:text-blueberry-400 dark:border-blueberry-700';
export const TOOLBAR_BUTTON_BASE = 'flex items-center gap-1 px-2.5 py-1 rounded-md border text-[11.5px]';

/**
 * Searchable group-by field picker — the third of the three consistent
 * toolbar popovers (Filters / Columns / Group by), hosted in
 * `ResponsivePopover` so it becomes a bottom sheet under 720px like the
 * other two. Up to `MAX_GROUP_LEVELS` ORDERED levels (PR1 U4/D7/D8, R5):
 * the popover shows one row per chosen level (its label + a per-level
 * remove button) and, while there is room for another, an "+ Add level"
 * row that switches to the same searchable field list this control has
 * always had — minus whatever keys are already chosen. Every pick/removal
 * commits immediately (no staged state anywhere in this toolbar, same as
 * before).
 *
 * Zero chosen levels skips straight to the searchable list (no "+ Add
 * level" row to click first) — there is nothing yet to summarize, and it
 * keeps the single-level "open Group, click a field" flow byte-identical
 * to before this unit.
 *
 * EXPORTED (U13/A6) for the Plays table's own toolbar, which offers the
 * same control over its own three group keys (Type / Status / City) — Plays
 * itself only ever drives ONE level through this picker
 * (`playsTableRows.ts`'s `orderPlaysForTable` stays single-level by design).
 * The Plays subtab renders BOTH toolbars on one page, so that caller passes
 * `triggerAriaLabel` to disambiguate the two otherwise-identical "Group"
 * buttons — the Plays mount (`plays/playsTableConfig.tsx`) names its Filters trigger "Play
 * filters". Omitted here (and everywhere else), the accessible name stays
 * the visible text.
 *
 * SINGLE-LEVEL MODE (`maxLevels === 1`, round-1 fix R12 / D-H) is the
 * pre-U4 Plays UX, restored: the popover opens STRAIGHT into the searchable
 * list (there is never a "next" level to summarize), a pick REPLACES the one
 * level rather than appending, the current level is listed and highlighted
 * instead of filtered out, and a single "Clear grouping" row stands in for
 * the per-level ✕. U4's generalization shipped "+ Add level" on Plays, which
 * `PlaysSubtab` could not honour (it keeps `levels[0]`), so picking a second
 * field silently did nothing and the rep had to remove the first level by
 * hand before they could switch.
 *
 * REORDER + SWAP (qa-batch5 D-D, multi-level mode only): each summary row
 * also carries a drag grip, Move up/down buttons, and its `{i+1}. {label}`
 * text is itself a button. Dragging a row (native HTML5 drag, copied from
 * `ColumnsPopover.tsx` — no new dependency) or pressing Move up/down calls
 * the pure `moveLevel` helper and commits immediately via `onChange`, same
 * as every other control here (no staged state). Clicking a level's name
 * sets `swapIndex` and opens the SAME searchable field list "+ Add level"
 * uses, except: the level being replaced stays listed and marked (like
 * single-level mode's `isCurrent`) instead of hidden, and a pick REPLACES
 * that one level in place (`levels.map` at `swapIndex`) rather than
 * appending — picking the marked level itself is a no-op. The trigger
 * click and the popover's `onClose` both reset `swapIndex` to `null`.
 */
export function GroupByPicker({
  columns, groupBy, onChange, triggerAriaLabel, maxLevels = MAX_GROUP_LEVELS,
}: {
  columns: Array<{ key: string; label: string; hint?: string }>;
  groupBy?: string[];
  onChange: (levels: string[]) => void;
  triggerAriaLabel?: string;
  /** Cap on ordered levels (default `MAX_GROUP_LEVELS`). `1` selects the
   *  single-level mode described above. */
  maxLevels?: number;
}) {
  const levels = groupBy ?? [];
  const singleLevel = maxLevels === 1;
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  // Which sub-view is showing: the chosen-levels summary, or the searchable
  // field list for picking the NEXT level. Re-derived every time the
  // popover opens (below), not just on mount. Single-level mode has no
  // summary sub-view at all, so it is pinned open on the list.
  const [addingLevel, setAddingLevel] = useState(singleLevel || levels.length === 0);
  // qa-batch5 D-D — index of the level being swapped via "click its name",
  // or `null` outside that flow. Multi-level only (single-level already has
  // its own "current key stays listed" path via `singleLevel` below).
  const [swapIndex, setSwapIndex] = useState<number | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  // Native HTML5 drag reorder (qa-batch5 D-D) — copied from
  // `ColumnsPopover.tsx`'s Shown-list pattern: state lives in a closure ref,
  // not `DataTransfer`, so jsdom's synthetic drag events are a faithful test.
  const dragFromRef = useRef<number | null>(null);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);

  const labelFor = (key: string): string => columns.find(c => c.key === key)?.label ?? key;
  const searched = q.trim()
    ? columns.filter(c => matchesFieldQuery(c, q))
    : columns;
  // Whether the current sub-view marks one key instead of hiding it —
  // single-level mode always does (there is no other sub-view); multi-level
  // mode does it only while swapping a specific level in place.
  const markCurrent = singleLevel || swapIndex !== null;
  // The OTHER already-chosen keys to hide — every chosen key normally, minus
  // the one being swapped (it stays listed, marked, so the rep can see what
  // they're replacing and can no-op back onto it).
  const otherChosen = singleLevel ? [] : swapIndex !== null ? levels.filter((_, j) => j !== swapIndex) : levels;
  const matches = singleLevel ? searched : searched.filter(c => !otherChosen.includes(c.key));

  const handleDrop = (toIndex: number) => {
    const from = dragFromRef.current;
    dragFromRef.current = null;
    setDraggingIndex(null);
    if (from === null || from === toIndex) return;
    onChange(moveLevel(levels, from, toIndex));
  };

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        aria-label={triggerAriaLabel}
        aria-expanded={open}
        onClick={() => {
          setOpen(o => !o);
          setQ('');
          setSwapIndex(null);
          setAddingLevel(singleLevel || levels.length === 0);
        }}
        className={`${TOOLBAR_BUTTON_BASE} ${levels.length > 0 ? TOOLBAR_BUTTON_ACTIVE : TOOLBAR_BUTTON_IDLE}`}
      >
        <Layers className="w-3.5 h-3.5" />
        {levels.length === 0 ? 'Group' : `Group: ${levels.map(labelFor).join(', ')}`}
      </button>
      <ResponsivePopover
        open={open}
        onClose={() => { setOpen(false); setSwapIndex(null); }}
        anchorRef={triggerRef}
        ariaLabel="Group by"
        className="w-60 overflow-hidden"
      >
        {!addingLevel ? (
          <div className="py-1">
            {levels.map((key, i) => (
              <div
                key={key}
                data-testid={`group-level-row-${key}`}
                draggable={!singleLevel}
                onDragStart={e => {
                  // Firefox never initiates HTML5 drag without a `setData`
                  // call in `dragstart` (ColumnsPopover's F17); jsdom's
                  // synthetic `dragStart` doesn't always carry a
                  // `dataTransfer`, hence the optional chaining.
                  e.dataTransfer?.setData('text/plain', key);
                  if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
                  dragFromRef.current = i;
                  setDraggingIndex(i);
                }}
                onDragEnd={() => {
                  dragFromRef.current = null;
                  setDraggingIndex(null);
                }}
                onDragOver={e => e.preventDefault()}
                onDrop={e => {
                  e.preventDefault();
                  handleDrop(i);
                }}
                className={`flex cursor-grab items-center gap-1 pl-1 pr-1.5 py-1 text-[12px] text-content-action dark:text-blueberry-400 font-medium ${
                  draggingIndex === i ? 'opacity-40' : ''
                }`}
              >
                <span
                  className="flex-shrink-0 text-[11px] leading-none text-content-tertiary dark:text-nightshade-500"
                  aria-hidden="true"
                >
                  ⣿
                </span>
                <button
                  type="button"
                  aria-label={`Change ${labelFor(key)} grouping level`}
                  onClick={() => { setSwapIndex(i); setQ(''); setAddingLevel(true); }}
                  className="flex-1 truncate text-left hover:underline"
                >
                  {i + 1}. {labelFor(key)}
                </button>
                <button
                  type="button"
                  aria-label={`Move ${labelFor(key)} grouping level up`}
                  disabled={i === 0}
                  onClick={() => onChange(moveLevel(levels, i, i - 1))}
                  className="shrink-0 px-0.5 text-content-tertiary dark:text-nightshade-500 hover:text-content-primary dark:hover:text-nightshade-100 disabled:opacity-30 disabled:hover:text-content-tertiary dark:disabled:hover:text-nightshade-500"
                >
                  <ChevronUp className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  aria-label={`Move ${labelFor(key)} grouping level down`}
                  disabled={i === levels.length - 1}
                  onClick={() => onChange(moveLevel(levels, i, i + 1))}
                  className="shrink-0 px-0.5 text-content-tertiary dark:text-nightshade-500 hover:text-content-primary dark:hover:text-nightshade-100 disabled:opacity-30 disabled:hover:text-content-tertiary dark:disabled:hover:text-nightshade-500"
                >
                  <ChevronDown className="w-3 h-3" />
                </button>
                <button
                  type="button"
                  aria-label={`Remove ${labelFor(key)} grouping level`}
                  onClick={() => onChange(levels.filter(k => k !== key))}
                  className="shrink-0 px-1 text-content-tertiary dark:text-nightshade-500 hover:text-content-primary dark:hover:text-nightshade-100"
                >
                  ✕
                </button>
              </div>
            ))}
            {levels.length < maxLevels && (
              <button
                type="button"
                onClick={() => { setQ(''); setAddingLevel(true); }}
                className="block w-full text-left px-2.5 py-1 text-[12px] text-content-secondary dark:text-nightshade-400 hover:bg-bg-secondary dark:hover:bg-nightshade-700"
              >
                + Add level
              </button>
            )}
          </div>
        ) : (
          <>
            <input
              autoFocus
              {...AUTOFILL_IGNORE_PROPS}
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search fields…"
              aria-label="Search fields"
              className="w-full px-2.5 py-1.5 text-[12px] border-b border-secondary dark:border-nightshade-700 bg-white dark:bg-nightshade-800 text-content-primary dark:text-nightshade-100 placeholder:text-content-placeholder outline-none"
            />
            <div className="max-h-64 overflow-auto py-1">
              {matches.map(c => {
                const isCurrent = singleLevel ? levels[0] === c.key : swapIndex !== null && levels[swapIndex] === c.key;
                return (
                  <button
                    key={c.key}
                    type="button"
                    // Single-level mode, or multi-level while swapping a
                    // level in place: the one chosen key is marked (and
                    // tinted) rather than hidden — see the component doc.
                    aria-selected={markCurrent ? isCurrent : undefined}
                    aria-label={c.label}
                    aria-description={c.hint}
                    onClick={() => {
                      if (swapIndex !== null) {
                        // Swap: REPLACE that one level in place; picking the
                        // marked (current) key itself is a no-op.
                        if (c.key !== levels[swapIndex]) {
                          onChange(levels.map((k, j) => (j === swapIndex ? c.key : k)));
                        }
                        setSwapIndex(null);
                        setQ('');
                        setAddingLevel(false);
                        return;
                      }
                      // REPLACE in single-level mode, APPEND otherwise.
                      onChange(singleLevel ? [c.key] : [...levels, c.key]);
                      setQ('');
                      if (singleLevel) setOpen(false);
                      else setAddingLevel(false);
                    }}
                    className={`block w-full text-left px-2.5 py-1 text-[12px] hover:bg-bg-secondary dark:hover:bg-nightshade-700 ${
                      isCurrent
                        ? 'text-content-action dark:text-blueberry-400 font-medium bg-bg-blue-light dark:bg-nightshade-700'
                        : 'text-content-primary dark:text-nightshade-100'
                    }`}
                  >
                    {c.label}
                    {c.hint && (
                      <span data-testid="field-hint" className="block truncate text-[11px] font-normal text-content-tertiary dark:text-nightshade-500">
                        {c.hint}
                      </span>
                    )}
                  </button>
                );
              })}
              {matches.length === 0 && (
                <div className="px-2.5 py-2 text-[11.5px] text-content-tertiary dark:text-nightshade-500">No fields match</div>
              )}
            </div>
            {/* Single-level mode's stand-in for the per-level ✕ — there is no
                summary sub-view here to host one. Hidden while ungrouped:
                there would be nothing to clear. */}
            {singleLevel && levels.length > 0 && (
              <button
                type="button"
                onClick={() => { onChange([]); setQ(''); setOpen(false); }}
                className="block w-full text-left px-2.5 py-1.5 text-[12px] border-t border-secondary dark:border-nightshade-700 text-content-secondary dark:text-nightshade-400 hover:bg-bg-secondary dark:hover:bg-nightshade-700"
              >
                Clear grouping
              </button>
            )}
          </>
        )}
      </ResponsivePopover>
    </div>
  );
}

/**
 * Generic Sort picker — the fourth toolbar popover (QA batch 2 / D-B),
 * rendered next to `GroupByPicker` only when the mount's config declares
 * `sortOptions`. Signals is the first consumer; every other mount is
 * unaffected because `config.sortOptions` stays undefined for them.
 *
 * `FlowTable` already has a sort — header clicks call `onSortChange` — this
 * control is a second entry point onto the SAME prop, so dirty/Reset/Save/
 * `flow_views.sort` all keep working without any extra plumbing here: this
 * component owns no state beyond "is the popover open."
 *
 * NEVER BLANK: `current` only matches when the working sort is literally one
 * of the configured options (same `key` AND `dir`) — a header click on a
 * column that isn't one of them (e.g. Account) leaves `current` undefined,
 * and the trigger falls back to `<column label> <arrow>` from `columns`
 * rather than rendering an option label that doesn't describe the real sort.
 *
 * "Active" (highlighted trigger) tracks whether the CURRENT sort differs from
 * the active view's OWN preset/saved sort (`defaultSort`) — not whether an
 * option is selected — so a header-click sort reads as "changed" the exact
 * same way `ViewDirtyBox` does, and reverts to idle the moment the rep picks
 * (or Resets back to) that view's sort again.
 */
export function SortPicker({
  options, sort, defaultSort, columns, onChange,
}: {
  options: FlowSortOption[];
  sort: { field: string; dir: 'ASC' | 'DESC' };
  defaultSort: { field: string; dir: 'ASC' | 'DESC' } | null;
  columns: Array<{ key: string; label: string }>;
  onChange: (sort: { field: string; dir: 'ASC' | 'DESC' }) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const triggerLabel = sortTriggerLabel(sort, options, columns);
  const isActive = !defaultSort || sort.field !== defaultSort.field || sort.dir !== defaultSort.dir;

  return (
    <div className="relative inline-block">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        className={`${TOOLBAR_BUTTON_BASE} ${isActive ? TOOLBAR_BUTTON_ACTIVE : TOOLBAR_BUTTON_IDLE}`}
      >
        <ArrowUpDown className="w-3.5 h-3.5" />
        {`Sort: ${triggerLabel}`}
      </button>
      <ResponsivePopover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={triggerRef}
        ariaLabel="Sort by"
        className="w-52"
      >
        <div className="py-1">
          {options.map((o) => {
            const checked = o.key === sort.field && o.dir === sort.dir;
            return (
              <button
                key={`${o.key}-${o.dir}`}
                type="button"
                role="menuitemradio"
                aria-checked={checked}
                onClick={() => {
                  onChange({ field: o.key, dir: o.dir });
                  setOpen(false);
                }}
                className={`block w-full text-left px-2.5 py-1 text-[12px] hover:bg-bg-secondary dark:hover:bg-nightshade-700 ${
                  checked
                    ? 'text-content-action dark:text-blueberry-400 font-medium bg-bg-blue-light dark:bg-nightshade-700'
                    : 'text-content-primary dark:text-nightshade-100'
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </ResponsivePopover>
    </div>
  );
}

export function FlowTable<Row extends Record<string, unknown>>(props: FlowTableProps<Row>) {
  const {
    views, viewMachine, onBrowseAllViews, config, rows, isLoading, isRefetching, error, hasMore, onLoadMore, isLoadingMore,
    activeViewId, onViewChange, sort, onSortChange, filters, onFiltersChange,
    pinnedConditions,
    filtersBypassed = false, onRestoreFilters,
    groupLogic, onGroupLogicChange, search, onSearchChange,
    groupBy, onGroupByChange, maxGroupLevels, visibleColumns, onVisibleColumnsChange,
    columnWidths, onColumnWidthsChange, totalLoaded,
    identity,
    emptyState,
    fillHeight = false,
    initialExpandedId,
    expandedId: controlledExpandedId,
    onExpandedIdChange,
    maxHeightClassName,
    minHeightClassName,
    loadMoreLabel,
    toolbarTrailing,
    dirtyBox,
    groupCollapseCommand,
  } = props;

  // Controlled/uncontrolled split (deep-link seam, e.g. `?contact=`): when
  // the caller passes `expandedId` (even `null`), FlowTable defers to it and
  // reports changes via `onExpandedIdChange` instead of tracking its own
  // state — see the prop's doc comment above.
  const [uncontrolledExpandedId, setUncontrolledExpandedId] = useState<string | null>(null);
  const isControlled = controlledExpandedId !== undefined;
  const expandedId = isControlled ? (controlledExpandedId ?? null) : uncontrolledExpandedId;
  const setExpandedId = useCallback((next: string | null) => {
    if (!isControlled) setUncontrolledExpandedId(next);
    onExpandedIdChange?.(next);
  }, [isControlled, onExpandedIdChange]);

  // ── Selection (U3 / R1, R2) ─────────────────────────────────────────────
  // The shift-click anchor is the ONLY selection-related state FlowTable
  // itself owns — "last explicitly toggled row id" (plan decision, U3), kept
  // inside `useRowSelectionGestures` (called below, once `loadedIds` exists).
  // The actual selected-id Set is fully controlled (config.selection.selectedIds),
  // owned by the caller.
  const selection = config.selection;

  // Group sizes, computed ONCE per render. The previous inline
  // `rows.filter(...)` ran inside the per-row map, making grouping O(n²)
  // over the loaded set — ~4M iterations per render at 2,000 grouped rows
  // (flagged in PR #1088 review). null when grouping is off.
  // `config.groupValue` (U14 / A11, A15) lets a mount group by a DERIVED
  // value — Plays folds several raw `play_type` exposure slugs into one
  // human bucket. Falls through to `row[key]` when absent, so every existing
  // mount is bit-identical. All three grouping sites (counts here, the header
  // boundary + label below) read through this one helper so they can never
  // disagree about what a group IS.
  const rawGroupValue = config.groupValue;
  const groupValueFor = useCallback(
    (row: Row, key: string): string => groupValueOf(rawGroupValue ? rawGroupValue(row, key) : row[key]),
    [rawGroupValue],
  );

  // Ordered grouping levels (PR1 U4/D7/D8, R5) — `[]` when ungrouped, up to
  // `MAX_GROUP_LEVELS`. Every grouping computation below is over this tuple
  // rather than one key: nesting is the SAME adjacency algorithm generalized
  // to a value tuple, not a new rendering path (bead's Architectural Context).
  const groupLevels = groupBy ?? EMPTY_GROUP_LEVELS;

  // Per-level, per-PATH-PREFIX counts, keyed by the tuple of stringified
  // group values up to and including that level, joined with
  // `GROUP_PATH_SEP` (D7 — a human delimiter could collide with a real group
  // value). A 3-level grouping therefore populates 3 map entries per row
  // (one per prefix length), same total work as before per level.
  const groupCounts = useMemo(() => {
    if (groupLevels.length === 0) return null;
    const counts = new Map<string, number>();
    for (const r of rows) {
      const path = groupLevels.map(k => groupValueFor(r, k));
      for (let k = 0; k < path.length; k++) {
        const key = path.slice(0, k + 1).join(GROUP_PATH_SEP);
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }
    return counts;
  }, [rows, groupLevels, groupValueFor]);

  // Collapsible group sections (6b, generalized to nested levels by PR1
  // U4/D7/D8). Ephemeral component state ONLY — this must never enter
  // `FlowWorkingStateSchema`/`flow_view_state` (that schema is the
  // dirty-diff shape; collapsing a section is not a "change" worth lighting
  // the Modified banner over). Keyed by the SAME path-prefix string
  // `groupCounts` uses, not by row id, so collapsing survives new rows
  // loading into an already-open group (`onLoadMore`).
  //
  // The default-collapsed rule is `config.defaultGroupCollapsed` when the
  // mount supplies one, else the same tracker/Snoozed rule this always had
  // (now expressed as the level-0 fallback) — a pure function computed
  // inline below, no seeding effect needed for it. The user's manual
  // overrides DO need to reset when the LEVELS change (a stale "I collapsed
  // Foo" from a previous grouping shouldn't survive into an unrelated one)
  // — done inline during render (React's "adjusting state when a prop
  // changes" pattern: compare against a previous-value state and reset
  // synchronously in the same render, before paint) rather than in a
  // `useEffect`, both because the plan calls for avoiding a seeding effect
  // and because `terra/no-derived-state-in-effect` flags exactly this shape
  // (an effect whose only job is one setState call).
  //
  // The reset key carries the ACTIVE VIEW as well as the levels (round-1 fix
  // R30). Keyed on the levels alone, switching to a different view that
  // happens to group by the same keys carried the previous view's manual
  // collapses over — a rep opened a fresh view and found sections already
  // folded shut. A view switch is a new table as far as collapse state goes,
  // even when the grouping tuple is byte-identical. The space in the key is
  // safe as a delimiter here because only the PAIR matters, never its parts.
  const [collapsedOverrides, setCollapsedOverrides] = useState<Map<string, boolean>>(new Map());
  const collapseResetKey = `${activeViewId} ${groupLevels.join(',')}`;
  const [prevCollapseResetKey, setPrevCollapseResetKey] = useState(collapseResetKey);
  // External Expand/Collapse-all base (D-B): `null` = no command in force,
  // the default rule decides. Seeded from a command already present at mount
  // (e.g. FlowTable remounting under a live command) so the table and the
  // caller's button label never disagree; `prevCommandSeq` starts at that
  // seq so the block below doesn't re-apply it.
  const [baseCollapsed, setBaseCollapsed] = useState<boolean | null>(
    () => (groupCollapseCommand ? groupCollapseCommand.mode === 'collapse' : null),
  );
  const [prevCommandSeq, setPrevCommandSeq] = useState<number | null>(groupCollapseCommand?.seq ?? null);
  if (collapseResetKey !== prevCollapseResetKey) {
    setPrevCollapseResetKey(collapseResetKey);
    setCollapsedOverrides(new Map());
    setBaseCollapsed(null);
  }
  // Same render-time "adjust state when a prop changes" pattern as the reset
  // above — no effect, no lifetime ref. Runs AFTER the reset so a command
  // arriving in the same render as a view/levels change still wins.
  if (groupCollapseCommand && groupCollapseCommand.seq !== prevCommandSeq) {
    setPrevCommandSeq(groupCollapseCommand.seq);
    setBaseCollapsed(groupCollapseCommand.mode === 'collapse');
    setCollapsedOverrides(new Map());
  }

  const isGroupCollapsed = (pathKey: string, level: number, value: string): boolean =>
    resolveGroupCollapsed({
      viewId: activeViewId,
      pathKey,
      level,
      value,
      overrides: collapsedOverrides,
      baseCollapsed,
      defaultRule: config.defaultGroupCollapsed,
      levels: groupLevels,
    });

  const toggleGroupCollapsed = (pathKey: string, level: number, value: string) => {
    setCollapsedOverrides(prev => {
      const next = new Map(prev);
      next.set(pathKey, !isGroupCollapsed(pathKey, level, value));
      return next;
    });
  };

  /** True when some STRICT ancestor of `level` (0..level-1) is collapsed —
   *  a collapsed group hides every descendant header AND every descendant
   *  row (D7/D8), not just its own direct rows. Pass `groupLevels.length`
   *  to also account for the row's OWN deepest level (i.e. "is this row
   *  itself inside a collapsed group at any level, including its own"). */
  const ancestorCollapsed = (path: string[], uptoExclusive: number): boolean => {
    for (let j = 0; j < uptoExclusive; j++) {
      const key = path.slice(0, j + 1).join(GROUP_PATH_SEP);
      if (isGroupCollapsed(key, j, path[j])) return true;
    }
    return false;
  };

  const [showFilters, setShowFilters] = useState(false);
  const filtersTriggerRef = useRef<HTMLButtonElement>(null);
  // Alternating row shading (zebra striping) — OFF by default (single color).
  // The TOGGLE lives in the Flow shell header (Command Center tab
  // only); this table just consumes the shared preference (zebraStripes.ts).
  const zebra = useZebraStripes();

  // Filter staging (QA-2 reversal, plan V2; GTMOPS-2216 REVERSAL; Phase 4
  // render-isolation follow-up) — the draft lives in `FilterDraftProvider`,
  // rendered as the ROOT of this component's returned JSX below, not called
  // as a hook HERE in `FlowTable`'s own render. It used to be: one
  // `useFilterDraft()` call right in this function body, so `setDraftFilters`
  // firing on every keystroke into a filter value input re-rendered
  // `FlowTable` itself — and with it the whole table body (up to ~500 rows,
  // no memoization anywhere in `components/flow/*.tsx`). Hoisting the hook
  // into a provider around this component's JSX fixes that: that JSX is
  // `FilterDraftProvider`'s `children` prop, created by THIS render and
  // passed down unchanged, so a draft update re-renders only the provider and
  // whichever descendants read the context (`FilterPopover`, the
  // `FilterDraftBox` banner below) — React bails out of the unchanged
  // `children` subtree. See `filters/useFilterDraft.ts`'s module doc for why
  // the draft needs to outlive the popover in the first place (GTMOPS-2216:
  // `ResponsivePopover` unmounts its children on close).

  // External-signal sync: the deep-link target (a prop, not query data) sets
  // the expansion once per value; the user keeps full control afterwards.
  //
  // Which deep-link target we've already scrolled to. Re-armed synchronously in render when the
  // target changes (so the ref callback attached during THIS commit sees the new state), never in
  // an effect — a passive-effect reset runs AFTER the commit/layout phase that (re-)attaches
  // `initialRowRef` to the target `<tr>`, so an effect-based reset always loses the race when
  // `initialExpandedId` changes while the table is already mounted (e.g. switching between two
  // already-open contacts silently skips the scroll). `useLayoutEffect` does not fix this either —
  // layout effects run child-first, same as ref attachment, so it's still a race.
  //
  // Both trackers are STATE, not refs, on purpose. The reset below runs in the render body, and a
  // plain ref mutated during render is not safe under React's concurrent rendering: if React
  // starts a render, executes this branch, then abandons that attempt before committing (e.g.
  // pre-empted by a higher-priority update), a `ref.current = null` has already happened and
  // persists, while the sibling `setPrevInitialExpandedId` does NOT (that render never committed).
  // The two trackers desync and a later render can double-scroll a target that never actually
  // changed. "Adjust state during render" is React's own documented pattern for this — state
  // updates queued in an abandoned render are discarded together, consistently — so routing the
  // scrolled-to tracker through `useState` makes the guard correct by construction. The one extra
  // re-render after a scroll (to commit `scrolledToInitialId`) is the accepted cost. The imperative
  // `scrollIntoView` still lives in the ref callback, which only fires during commit, so it is not
  // subject to the abandoned-render hazard.
  const [scrolledToInitialId, setScrolledToInitialId] = useState<string | null>(null);
  const [prevInitialExpandedId, setPrevInitialExpandedId] = useState(initialExpandedId);
  if (initialExpandedId !== prevInitialExpandedId) {
    setPrevInitialExpandedId(initialExpandedId);
    if (scrolledToInitialId !== null) setScrolledToInitialId(null);
  }
  // Uncontrolled-mode seed only. In controlled mode the URL (`expandedId`) already carries the
  // value — setting it again here would just echo it back through `onExpandedIdChange`.
  useEffect(() => {
    if (initialExpandedId && !isControlled) setExpandedId(initialExpandedId);
  }, [initialExpandedId, isControlled, setExpandedId]);
  // Ref callback: scroll the deep-linked row into view once per new target, when it (re-)mounts.
  const initialRowRef = (node: HTMLTableRowElement | null) => {
    if (node && initialExpandedId && scrolledToInitialId !== initialExpandedId) {
      setScrolledToInitialId(initialExpandedId);
      node.scrollIntoView({ block: 'center' });
    }
  };

  // Measure the horizontal-scroll container so the sticky expanded detail
  // fills exactly the visible width (a CSS 100vw calc can't know the chrome).
  const scrollRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | null>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => setContainerWidth(el.clientWidth);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const colByKey = useMemo(
    () => Object.fromEntries(config.columns.map(c => [c.key, c])) as Record<string, FlowColumnDef<Row>>,
    [config.columns],
  );
  const cols = visibleColumns.map(k => colByKey[k]).filter(Boolean);
  const sectionHeaders = config.groupHeaderStyle === 'section';
  /**
   * Floating group cards (visual QA follow-up on Q3's grouped-table output).
   * Only when grouped AND in section style: an ungrouped section-style table
   * has no top-level groups to carve into cards, and 'compact' (Contacts/
   * Accounts/Plays) keeps today's one-bordered-table look unconditionally.
   */
  const cardMode = sectionHeaders && groupLevels.length > 0;
  const totalCols = cols.length + 1 + (selection ? 1 : 0) + (config.tracker ? 1 : 0) + (config.rowAction ? 1 : 0);
  /**
   * Card mode's physical column order — the same order the `<thead>` and
   * every data row render in — so the column header, group header rows and
   * data rows can all ask `cardCellEdges` for the same per-column dividers.
   */
  const cardColumns: CardColumnKind[] = cardMode
    ? [
        ...(selection ? ['select' as const] : []),
        'chevron' as const,
        ...(config.tracker ? ['tracker' as const] : []),
        ...cols.map(() => 'data' as const),
        ...(config.rowAction ? ['action' as const] : []),
      ]
    : [];
  /** Stable React keys for `cardColumns`, one per entry (column identity, never position). */
  const cardColumnKeys: string[] = cardMode
    ? [
        ...(selection ? ['__select'] : []),
        '__chevron',
        ...(config.tracker ? ['__tracker'] : []),
        ...cols.map(c => c.key),
        ...(config.rowAction ? ['__action'] : []),
      ]
    : [];
  /**
   * Card mode's loading / error / empty row: closes the card the sticky
   * column header opens (its rounded top corners), since the floating-card
   * table has no outer border of its own. These edges ride the viewport-
   * pinned `sticky left-0` div inside `spanRow` below, not the table-wide
   * colSpan `<td>` — a colSpan cell spans the table's TRUE width, which
   * extends past the visible viewport once the table scrolls horizontally,
   * so a border painted on it lands off-screen (fix-plan batch 5, D3).
   */
  const cardStatusEdges = cardMode ? ` bg-white dark:bg-nightshade-900 border-l border-r border-b ${CARD_BORDER_COLOR} rounded-b-lg` : '';
  /**
   * Renders the loading / error / empty colSpan row. In card mode the text
   * classes + `cardStatusEdges` move onto the `sticky left-0` div (sized to
   * `containerWidth`, same as the expanded-detail div below) so the card's
   * edges land at the viewport's right edge — the same place the pinned
   * action-column cell puts them in every other row — instead of at the
   * table's true (often off-screen) right end. Compact mode is untouched:
   * `cardStatusEdges` is `''` there, so the div branch never renders and
   * the `<td>` keeps today's byte-for-byte markup (text as its direct
   * child).
   */
  const spanRow = (text: React.ReactNode, textClass: string) => (
    <tr>
      <td colSpan={totalCols} className={cardMode ? 'p-0' : textClass}>
        {cardMode ? (
          <div
            className={`sticky left-0 ${textClass}${cardStatusEdges}`}
            style={containerWidth ? { width: containerWidth } : undefined}
          >
            {text}
          </div>
        ) : text}
      </td>
    </tr>
  );
  /** Where a card-mode group header's label goes: the first data column (else the chevron column). */
  const cardLabelIdx = cardColumns.includes('data') ? cardColumns.indexOf('data') : cardColumns.indexOf('chevron');
  // renderDetail (expand) and onRowClick (navigate) are mutually exclusive;
  // renderDetail wins when both are set (documented in types.ts). `selection`
  // makes a row respond to Cmd/Ctrl/Shift-click even on a table with neither
  // (future-proofing — no current mount combines bare selection with
  // neither renderDetail nor onRowClick).
  const clickable = !!config.renderDetail || !!config.onRowClick || !!selection;

  // Row ids in CURRENTLY RENDERED order — the shift-click range and the
  // header "select all loaded" checkbox both resolve against this, not
  // against `config.selection.selectedIds` (which may include ids from
  // pages/filters no longer rendered). Cheap (`rows.length` per render, no
  // heavier than the existing `groupCounts` pass) so no memoization needed
  // beyond what `rows` itself already gets from the caller.
  const loadedIds = selection ? rows.map(config.rowKey) : [];
  const gestures = useRowSelectionGestures({ selection, loadedIds });
  const allLoadedSelected = !!selection && loadedIds.length > 0 && loadedIds.every(id => selection.selectedIds.has(id));
  const someLoadedSelected = !!selection && loadedIds.some(id => selection.selectedIds.has(id));

  // Column list for the picker. `group` is the catalog's object label, shown
  // as a chip ONLY for fields belonging to a different object than the
  // table's own (the prototype's `F[k].o !== 'Contact'` rule) — a chip on
  // every row of a single-object table is noise, not information.
  const primaryGroup = config.filterFields[0]?.group;
  const columnPickerColumns = useMemo<ColumnsPopoverColumn[]>(() => {
    const groupByKey = new Map(config.filterFields.map(f => [f.key, f.group]));
    return config.columns.map(c => {
      const group = groupByKey.get(c.key);
      return {
        key: c.key, label: c.label, group: group && group !== primaryGroup ? group : undefined,
        hint: fieldExamples(config.filterFields.find(f => f.key === c.key) ?? {}),
      };
    });
  }, [config.columns, config.filterFields, primaryGroup]);

  // fix-plan-batch3.md D-F — extracted to `groupableColumns.ts` so
  // `signalsTableConfig.test.tsx`'s forward-coverage test ("every key the
  // picker offers has a non-`—` blank label") can compute the SAME list
  // this memo renders, rather than a hand-maintained copy that drifts.
  const groupableColumns = useMemo(
    () => groupableColumnsOf({ columns: config.columns, tracker: config.tracker, groupOnlyKeys: config.groupOnlyKeys })
      .map(c => {
        const hint = fieldExamples(config.filterFields.find(f => f.key === c.key) ?? {});
        return hint ? { ...c, hint } : c;
      }),
    [config.columns, config.tracker, config.groupOnlyKeys, config.filterFields],
  );

  // QA batch 2 / D-B — the active view's OWN sort (a preset's default, or a
  // saved view's stored `sort`), used only to decide whether `SortPicker`'s
  // trigger reads as "changed" (the same "differs from the view" rule
  // `ViewDirtyBox` applies to the rest of the working state). `null` when the
  // view id doesn't resolve, which `SortPicker` treats as "always active"
  // rather than crashing or silently matching everything.
  const activeViewSort = useMemo(
    () => views.find(v => v.id === activeViewId)?.sort ?? null,
    [views, activeViewId],
  );

  // GTMOPS-2254: while filters are bypassed the query itself is unfiltered,
  // so the toolbar chip must read 0 rather than reporting a count that no
  // longer describes what's actually running.
  const activeFilterCount = countActiveFilters(filters, pinnedConditions, filtersBypassed);

  // ── Header drag-reorder (R4) ────────────────────────────────────────────
  // Native HTML5 drag-and-drop (Decision 11) on the <th>s. Drop-target state
  // lives in a ref rather than being read back from DataTransfer during
  // dragover (browser-inconsistent); `dataTransfer.setData` is still called
  // in `dragstart` because Firefox refuses to initiate a drag at all
  // without it (F17) — it's write-only here, never read.
  // `PINNED_COLUMN_KEY` is neither draggable nor a valid drop target, and
  // the emitted array is `[pinned, ...tail]` — the exact shape
  // ColumnsPopover's reorder emits.
  const dragKeyRef = useRef<string | null>(null);
  // A drag that ends in a drop must not also sort. Browsers fire no click
  // after a drop, but they DO fire `dragend`, which clears this flag — so it
  // can never leak into a later, unrelated header click.
  const suppressClickRef = useRef(false);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  // Which half of the hovered header the pointer is over. The drop
  // indicator (drawn from this state, below) and the splice math in
  // `dropOnHeader` both read the SAME value, so they can never disagree
  // (F16) — including landing in the last position via a right-half drop
  // on the last column.
  const [dragOverSide, setDragOverSide] = useState<ColumnDragSide | null>(null);

  // StrictMode: both drag lifetime refs are reset at the TOP of the mount
  // effect (not only in cleanup), so a dev double-invoke — or a real remount
  // mid-drag — starts from a clean slate instead of a stuck ref.
  useEffect(() => {
    dragKeyRef.current = null;
    suppressClickRef.current = false;
    setDragOverKey(null);
    setDragOverSide(null);
    return () => {
      dragKeyRef.current = null;
      suppressClickRef.current = false;
    };
  }, []);

  const headerClick = (col: FlowColumnDef<Row>) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    if (!isSortableColumn(col)) return;
    if (sort.field === col.key) {
      onSortChange({ field: col.key, dir: sort.dir === 'DESC' ? 'ASC' : 'DESC' });
    } else {
      onSortChange({ field: col.key, dir: defaultDirFor(col.type) });
    }
  };

  const startHeaderDrag = (key: string, e: React.DragEvent<HTMLTableCellElement>) => {
    // Firefox never initiates HTML5 drag without a `setData` call in
    // `dragstart` (F17). jsdom's synthetic `dragStart` events don't always
    // carry a `dataTransfer`, hence the optional chaining.
    e.dataTransfer?.setData('text/plain', key);
    if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move';
    dragKeyRef.current = key;
    suppressClickRef.current = true;
  };

  const endHeaderDrag = () => {
    dragKeyRef.current = null;
    suppressClickRef.current = false;
    setDragOverKey(null);
    setDragOverSide(null);
  };

  /** Which half of `e.currentTarget` the pointer is over — the single
   * source of truth for both the drop indicator and the splice side. */
  const sideForDragEvent = (e: React.DragEvent<HTMLTableCellElement>): ColumnDragSide => {
    const rect = e.currentTarget.getBoundingClientRect();
    return e.clientX < rect.left + rect.width / 2 ? 'before' : 'after';
  };

  const dropOnHeader = (toKey: string) => {
    const fromKey = dragKeyRef.current;
    const side = dragOverSide ?? 'before';
    dragKeyRef.current = null;
    setDragOverKey(null);
    setDragOverSide(null);
    if (!fromKey || fromKey === toKey) return;
    if (fromKey === PINNED_COLUMN_KEY || toKey === PINNED_COLUMN_KEY) return;
    const tail = visibleColumns.filter(k => k !== PINNED_COLUMN_KEY);
    const next = reorderColumns(tail, fromKey, toKey, side);
    onVisibleColumnsChange(
      visibleColumns.includes(PINNED_COLUMN_KEY) ? [PINNED_COLUMN_KEY, ...next] : next,
    );
  };

  // ── Header drag-RESIZE (U10 / R12) ──────────────────────────────────────
  // Hand-rolled and deliberately NOT HTML5 DnD, unlike the reorder above:
  // two gestures on the same <th> need two different transports or they
  // fight. A plain mouse gesture (mousedown on the handle → document-level
  // mousemove/mouseup) cannot start a reorder as long as the mousedown is
  // `preventDefault`ed, and the handle stops `dragstart`/`click` from
  // reaching the header at all, so neither reorder nor sort can fire from it.
  //
  // Widths are committed on every move, not on release: the write path is
  // `machine.setColumnWidths` → `useFlowViewStatePersist`'s ~1s debounce, so
  // live feedback costs nothing extra in requests and the column tracks the
  // pointer instead of snapping at the end.
  //
  // The pinned column IS resizable. `PINNED_COLUMN_KEY` pins against
  // REORDER (composition order), and it carries no sticky/left-offset
  // styling that an explicit width could fight — the only sticky cell in
  // this table is the trailing `rowAction` column, which has no header
  // handle because it isn't in `cols` at all.
  const resizeStopRef = useRef<(() => void) | null>(null);
  // StrictMode: detach at the TOP of the mount effect as well as in cleanup,
  // so a dev double-invoke (or a real remount mid-drag) can never leave a
  // document listener bound to a dead render's callback.
  useEffect(() => {
    resizeStopRef.current?.();
    resizeStopRef.current = null;
    return () => {
      resizeStopRef.current?.();
      resizeStopRef.current = null;
    };
  }, []);

  const startColumnResize = (key: string, e: React.MouseEvent<HTMLElement>) => {
    if (!onColumnWidthsChange) return;
    // preventDefault is load-bearing twice over: it stops the browser from
    // promoting this press into the <th>'s HTML5 drag, and it stops the
    // text selection a horizontal drag would otherwise paint across the row.
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const base: Record<string, number> = { ...columnWidths };
    // Zoom-safe (fix-plan Unit 4, mirrors `shell-next/table/LedgerTable.tsx`):
    // the V2 shell zooms its routed subtree (which includes V1 FlowTable
    // mounts), and under CSS `zoom` the rect width and `clientX` are visual
    // (zoomed) px while `offsetWidth` is the unzoomed layout width. Their
    // ratio is the effective zoom; the pointer delta and the measured rect are
    // divided by it so the column tracks the cursor. No `th`, a zero
    // `offsetWidth` (jsdom) or a zero rect all fall back to scale 1.
    // `offsetWidth` is integer-rounded, so an unzoomed fractional header
    // reads a ratio a hair off 1 (253.5625/254); `snapUnitZoom` snaps that
    // to exactly 1, and at exactly 1 the pre-fix math runs verbatim
    // (`startWidth = rect.width || MIN`, delta un-scaled) — V2-off parity.
    const th = e.currentTarget.closest('th');
    const offsetWidth = th?.offsetWidth ?? 0;
    const rectWidth = th?.getBoundingClientRect().width ?? 0;
    const zoom = snapUnitZoom(th && offsetWidth > 0 ? rectWidth / offsetWidth : 1);
    const unitZoom = zoom === 1;
    const effectiveZoom = zoom || 1;
    // Resume from the stored width when there is one; otherwise from what
    // the column currently measures (un-zoomed). `|| MIN` catches jsdom (no
    // layout, so every rect is 0) and any collapsed/hidden edge case.
    const startWidth = base[key] ?? (unitZoom
      ? (rectWidth || MIN_COLUMN_WIDTH_PX)
      : ((rectWidth / effectiveZoom) || offsetWidth || MIN_COLUMN_WIDTH_PX));

    const onMove = (ev: MouseEvent) => {
      const next = Math.max(MIN_COLUMN_WIDTH_PX, Math.round(startWidth + (ev.clientX - startX) / effectiveZoom));
      onColumnWidthsChange({ ...base, [key]: next });
    };
    const stop = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', stop);
      resizeStopRef.current = null;
    };
    resizeStopRef.current?.();
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', stop);
    resizeStopRef.current = stop;
  };

  /** Explicit width for a column, or undefined to leave today's CSS alone
   *  (`max-w-[280px] truncate` on cells, auto-layout on the header). */
  const widthStyleFor = (key: string): React.CSSProperties | undefined => {
    const w = columnWidths?.[key];
    return w === undefined ? undefined : { width: w, minWidth: w, maxWidth: w };
  };

  // Column-jump flash (U4): the whole column, not just the header, flashes so
  // a rep's eye lands on the right vertical band once the scroll settles.
  // Tracks its own pending-removal timer (a plain ref, not an effect — this
  // is fired imperatively from a click, there is no prop this could key an
  // effect off of) so a second jump — same column or a different one —
  // clears any still-running flash from the previous jump before starting
  // its own, instead of two timers racing to remove the class.
  const columnFlashTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (columnFlashTimeoutRef.current) clearTimeout(columnFlashTimeoutRef.current);
  }, []);

  /** ColumnsPopover click-to-jump: scroll the matching <th> horizontally
   * into view inside this table's own scroll box, then flash every cell in
   * that column (whole-column highlight, not just the header). */
  const jumpToColumn = useCallback((key: string) => {
    const th = scrollRef.current?.querySelector<HTMLElement>(`th[data-col-key="${key}"]`);
    th?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });

    if (columnFlashTimeoutRef.current) {
      clearTimeout(columnFlashTimeoutRef.current);
      columnFlashTimeoutRef.current = null;
    }
    // A rAF between the scroll and the flash so the browser has committed
    // the smooth-scroll's first frame before the animation starts — keeps
    // the two visually distinct instead of the flash appearing to get cut
    // short by scroll-induced layout thrash.
    requestAnimationFrame(() => {
      const cells = scrollRef.current?.querySelectorAll<HTMLElement>(`[data-col-key="${key}"]`);
      if (!cells || cells.length === 0) return;
      // Remove → force reflow → re-add: restarts the CSS animation even when
      // the same column is jumped to twice in a row (a bare re-add on an
      // element that already has the class is a no-op, matching the
      // `flow-row-flash` restart trick).
      cells.forEach(cell => cell.classList.remove('flow-col-flash'));
      void cells[0].offsetWidth;
      cells.forEach(cell => cell.classList.add('flow-col-flash'));
      // Consume (remove the class) on COMPLETION inside the timer, never in
      // a cleanup path — this mirrors QueueBody.tsx's flash timer hygiene:
      // an early cleanup-side removal would fight a rapid second jump into
      // the SAME column instead of just letting the new jump's own
      // remove→reflow→re-add supersede it.
      columnFlashTimeoutRef.current = setTimeout(() => {
        cells.forEach(cell => cell.classList.remove('flow-col-flash'));
        columnFlashTimeoutRef.current = null;
      }, 1600);
    });
  }, []);

  /**
   * Floating-card block boundaries (`cardMode` only) — for each TOP-LEVEL
   * (level 0) group, which single rendered element is the LAST one: almost
   * always the last data row, but a collapsed final sub-group renders only
   * its own header with nothing after it (bottom rounding then belongs to
   * THAT header), and a fully collapsed top-level group renders only its own
   * level-0 header (both the top AND bottom of its card).
   *
   * Re-walks the same boundary/collapse rules the render loop below applies
   * — hidden rows/headers render nothing in the DOM, so "what renders last"
   * can only be computed this way, not read back off refs. Recomputed every
   * render (not memoized): it's one O(rows × levels) pass, the same order of
   * work `groupCounts` above already does per render, and depending on live
   * collapse state (`isGroupCollapsed`) would otherwise need that entire
   * closure's inputs repeated as a dependency array for no real savings.
   */
  const blockBoundaries = (() => {
    if (!cardMode) return null;
    const bottomHeaderKeys = new Set<string>();
    const bottomRowIds = new Set<string>();
    let currentTopKey: string | null = null;
    let lastEntry: { kind: 'header'; pathKey: string } | { kind: 'row'; id: string } | null = null;
    const finalizeBlock = () => {
      if (!lastEntry) return;
      if (lastEntry.kind === 'header') bottomHeaderKeys.add(lastEntry.pathKey);
      else bottomRowIds.add(lastEntry.id);
    };
    rows.forEach((row, i) => {
      const path = groupLevels.map(k => groupValueFor(row, k));
      const prevPath = i > 0 ? groupLevels.map(k => groupValueFor(rows[i - 1], k)) : null;
      const boundaryAt = (k: number): boolean => {
        if (i === 0 || prevPath === null) return true;
        for (let j = 0; j <= k; j++) if (path[j] !== prevPath[j]) return true;
        return false;
      };
      if (path[0] !== currentTopKey) {
        finalizeBlock();
        currentTopKey = path[0];
        lastEntry = null;
      }
      for (let k = 0; k < groupLevels.length; k++) {
        if (!boundaryAt(k) || ancestorCollapsed(path, k)) continue;
        lastEntry = { kind: 'header', pathKey: path.slice(0, k + 1).join(GROUP_PATH_SEP) };
      }
      if (!ancestorCollapsed(path, groupLevels.length)) {
        lastEntry = { kind: 'row', id: String(config.rowKey(row)) };
      }
    });
    finalizeBlock();
    return { bottomHeaderKeys, bottomRowIds };
  })();

  return (
    <FilterDraftProvider
      filter={filters}
      onFiltersChange={onFiltersChange}
      groupLogic={groupLogic}
      onGroupLogicChange={onGroupLogicChange}
      fields={config.filterFields}
    >
    <div className={`flex flex-col min-h-0 ${fillHeight ? 'flex-1' : ''}`}>
      {/* Toolbar: view switcher + search + filter/column/group controls */}
      <div className="flex items-center gap-2 flex-wrap pb-2">
        {viewMachine ? (
          <ViewSwitcher machine={viewMachine} onBrowseAll={onBrowseAllViews} />
        ) : (
          /* Unbound fallback (no saved-views machine): the original flat
             preset pill row. Kept so FlowTable stays usable as a generic
             table — it is exported through mini-apps/_shared/components. */
          <div className="flex items-center gap-1 flex-wrap">
            {views.map(v => (
              <button
                key={v.id}
                onClick={() => onViewChange(v.id)}
                className={`px-2.5 py-1 rounded-full border text-[11.5px] whitespace-nowrap transition-colors ${
                  v.id === activeViewId
                    ? 'bg-content-primary text-white border-content-primary dark:bg-nightshade-100 dark:text-nightshade-900 dark:border-nightshade-100'
                    : 'bg-white dark:bg-nightshade-800 text-content-secondary dark:text-nightshade-400 border-secondary dark:border-nightshade-700 hover:bg-bg-secondary dark:hover:bg-nightshade-700'
                }`}
              >
                {v.name}
              </button>
            ))}
          </div>
        )}
        <div className="relative inline-block">
          <button
            ref={filtersTriggerRef}
            type="button"
            aria-expanded={showFilters}
            onClick={() => setShowFilters(s => !s)}
            className={`${TOOLBAR_BUTTON_BASE} ${
              activeFilterCount > 0 || showFilters ? TOOLBAR_BUTTON_ACTIVE : TOOLBAR_BUTTON_IDLE
            }`}
          >
            <ListFilter className="w-3.5 h-3.5" />
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>
          <ResponsivePopover
            open={showFilters}
            onClose={() => setShowFilters(false)}
            anchorRef={filtersTriggerRef}
            ariaLabel="Filters"
            withBackdrop
          >
            <FilterPopover
              filter={filters}
              onFiltersChange={onFiltersChange}
              groupLogic={groupLogic}
              onGroupLogicChange={onGroupLogicChange}
              fields={config.filterFields}
              identity={identity}
              pinned={pinnedConditions}
            />
          </ResponsivePopover>
        </div>
        <ColumnsPopover
          columns={columnPickerColumns}
          visibleColumns={visibleColumns}
          onChange={onVisibleColumnsChange}
          onJumpToColumn={jumpToColumn}
          pinnedKey={PINNED_COLUMN_KEY}
          triggerClassName={`${TOOLBAR_BUTTON_BASE} ${TOOLBAR_BUTTON_IDLE}`}
        />
        <GroupByPicker columns={groupableColumns} groupBy={groupBy} onChange={onGroupByChange} maxLevels={maxGroupLevels} />
        {config.sortOptions && config.sortOptions.length > 0 && (
          <SortPicker
            options={config.sortOptions}
            sort={sort}
            defaultSort={activeViewSort}
            columns={config.columns}
            onChange={onSortChange}
          />
        )}
        {config.showSearch !== false && (
          <div className="relative ml-auto">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-content-tertiary dark:text-nightshade-500" />
            <input
              {...AUTOFILL_IGNORE_PROPS}
              value={search}
              onChange={e => onSearchChange(e.target.value)}
              placeholder="Search…"
              className="pl-7 pr-2 py-1 text-[12px] border border-secondary dark:border-nightshade-700 rounded-md w-52 bg-white dark:bg-nightshade-800 text-content-primary dark:text-nightshade-100 placeholder:text-content-placeholder"
            />
          </div>
        )}
        {/* Prototype `#dirtyBox` — between the control popovers and the row
            count, hidden entirely while the view is clean. Grouped with the
            row count into one wrapper so the pair can carry the right-edge
            `ml-auto` themselves when the search box (the usual right anchor)
            isn't rendered — the dirty box alone can't own that class since it
            renders `null` while the view is clean.
            GTMOPS-2216: `FilterDraftBox` renders first — a closed-but-
            unapplied filter draft is the more urgent of the two "you have
            unsaved work" signals, since the rep may not even realize the
            popover closing dropped nothing (it didn't — this banner is why).
            Gated on `!showFilters` in addition to the box's own `isDirty`
            check so it's never shown WHILE the popover is open — the popover
            already renders its own "Unapplied changes" hint and Apply button
            in that case, and showing both at once is redundant.
            UNBOUND path (no `viewMachine`, e.g. Plays — no saved-view entity):
            `dirtyBox` renders in this SAME slot instead, so a caller-supplied
            `ViewDirtyPill` lines up exactly where `ViewDirtyBox` would. */}
        <div className={`flex items-center gap-2 ${config.showSearch === false ? 'ml-auto' : ''}`}>
          {!showFilters && <FilterDraftBox onEdit={() => setShowFilters(true)} />}
          {viewMachine ? <ViewDirtyBox machine={viewMachine} /> : dirtyBox}
          <span className="text-[11px] text-content-tertiary dark:text-nightshade-500 whitespace-nowrap">
            {totalLoaded} loaded{hasMore ? '+' : ''}
          </span>
          {/* A13 — Bulk actions (and any future trailing control) sits HERE,
              right of the count, rather than on its own strip above the
              table. Inside this wrapper on purpose: the wrapper carries the
              right-edge anchoring, so a trailing node placed outside it
              would drift left of the count. */}
          {toolbarTrailing}
        </div>
      </div>

      {/* GTMOPS-2254 — reversible notice while a search-originated deep-link
          has paused the active filters (see `filtersBypassed`'s doc comment
          above). Full-width row of its own between the toolbar and the
          table card, not squeezed into the toolbar's flex-wrap row. */}
      {filtersBypassed && (
        <div
          data-testid="filters-bypassed-banner"
          className="flex items-center gap-2 mb-2 px-2.5 py-1.5 rounded-md border border-content-action bg-bg-blue-light dark:border-blueberry-700 text-[11.5px] text-content-action dark:text-blueberry-400"
        >
          <span>Filters paused to show the contact you searched for</span>
          <button
            type="button"
            onClick={() => onRestoreFilters?.()}
            className="ml-auto font-medium underline hover:no-underline"
          >
            Restore filters
          </button>
        </div>
      )}

      {/*
        The table — dense, SFDC-report style. Dim during background refetch
        instead of flashing.

        overflow-auto (BOTH axes), not overflow-x-auto: per the CSS overflow
        spec, `overflow-x: auto` + `overflow-y: visible` is an invalid
        combination — the browser computes overflow-y as `auto` too regardless
        of what's written, which makes this div the sticky containing block
        for the <thead> below no matter what. Fighting that (the previous
        approach) just produced a wrong offset; embracing it — making this
        card the bounded, scrollable box in BOTH directions — is the standard
        sticky-table pattern and the only configuration where the pinned
        header lands in the right place. Bounded via `fillHeight` ? flex-fill
        (fills the parent's remaining space, requires the parent flex chain —
        see the fillHeight prop doc above) : a `max-h` viewport cap (default,
        for mid-page mounts). The <thead> sticks at `top: 0` WITHIN this card.
      */}
      <div
        ref={scrollRef}
        className={`${cardMode ? '' : 'border border-secondary dark:border-nightshade-700 rounded-lg bg-white dark:bg-nightshade-900 '}overflow-auto min-h-0 transition-opacity ${isRefetching ? 'opacity-60' : ''} ${
          fillHeight
            ? 'flex-1'
            // A18 — an optional floor alongside the cap; both are dropped
            // under `fillHeight`, which owns the height outright.
            : `${minHeightClassName ? `${minHeightClassName} ` : ''}${maxHeightClassName ?? 'max-h-[calc(100dvh-8rem)]'}`
        }`}
      >
        {/* Card mode is border-separate + zero spacing: collapsed borders
            ignore border-radius (the square outline around each rounded
            card corner). See `cardCellEdges` for how the dividers are
            re-expressed per cell so none double up. */}
        <table className={`w-full ${cardMode ? 'border-separate border-spacing-0' : 'border-collapse'} text-[12.5px]`}>
          <thead className="sticky top-0 z-10">
            <tr>
              {selection && (
                <th className={`w-9 px-2.5 bg-bg-secondary dark:bg-nightshade-800 ${
                  cardMode ? cardCellEdges(cardColumns, 0, { top: true, roundTop: true }) : 'border-b border-secondary dark:border-nightshade-700'
                }`}>
                  <Checkbox
                    aria-label="Select all loaded rows"
                    checked={allLoadedSelected}
                    indeterminate={someLoadedSelected && !allLoadedSelected}
                    onCheckedChange={() => selection.onToggleAllLoaded(loadedIds)}
                  />
                </th>
              )}
              <th className={`w-6 bg-bg-secondary dark:bg-nightshade-800 ${
                cardMode
                  ? cardCellEdges(cardColumns, cardColumns.indexOf('chevron'), { top: true, roundTop: true })
                  : 'border-b border-secondary dark:border-nightshade-700'
              }`} />
              {config.tracker && (
                <th className={`px-2.5 py-1.5 w-24 text-left text-[10.5px] uppercase tracking-wide font-medium text-content-tertiary dark:text-nightshade-500 bg-bg-secondary dark:bg-nightshade-800 ${
                  cardMode
                    ? cardCellEdges(cardColumns, cardColumns.indexOf('tracker'), { top: true, roundTop: true })
                    : 'border-b border-r border-secondary dark:border-nightshade-700'
                } whitespace-nowrap`}>
                  {config.tracker.label ?? 'Status'}
                </th>
              )}
              {cols.map((col, colIdx) => {
                // 'Name' is pinned by the composition rule — it can neither be
                // picked up nor dropped onto (the same rule ColumnsPopover
                // enforces on its "Shown" list).
                const draggable = col.key !== PINNED_COLUMN_KEY;
                const widthStyle = widthStyleFor(col.key);
                return (
                  <th
                    key={col.key}
                    data-col-key={col.key}
                    // Optional per-column header tooltip (FlowColumnDef.headerTitle).
                    title={col.headerTitle}
                    style={widthStyle}
                    draggable={draggable}
                    onDragStart={draggable ? (e => startHeaderDrag(col.key, e)) : undefined}
                    onDragEnd={draggable ? endHeaderDrag : undefined}
                    onDragOver={draggable ? (e => {
                      e.preventDefault();
                      setDragOverKey(col.key);
                      setDragOverSide(sideForDragEvent(e));
                    }) : undefined}
                    onDragLeave={draggable ? (() => {
                      if (dragOverKey === col.key) {
                        setDragOverKey(null);
                        setDragOverSide(null);
                      }
                    }) : undefined}
                    onDrop={draggable ? (e => { e.preventDefault(); dropOnHeader(col.key); }) : undefined}
                    onClick={() => headerClick(col)}
                    className={`relative px-2.5 py-1.5 text-left text-[10.5px] uppercase tracking-wide font-medium text-content-tertiary dark:text-nightshade-500 bg-bg-secondary dark:bg-nightshade-800 ${
                      cardMode
                        ? cardCellEdges(cardColumns, cardLabelIdx + colIdx, { top: true, roundTop: true })
                        : 'border-b border-r border-secondary dark:border-nightshade-700'
                    } whitespace-nowrap select-none ${
                      isSortableColumn(col) ? 'cursor-pointer hover:text-content-primary dark:hover:text-nightshade-100' : ''
                    } ${dragOverKey === col.key && dragOverSide === 'before' ? 'border-l-2 border-l-content-action' : ''} ${
                      dragOverKey === col.key && dragOverSide === 'after' ? 'border-r-2 border-r-content-action' : ''
                    } ${col.numeric ? 'text-right' : ''} ${widthStyle ? 'overflow-hidden text-ellipsis' : ''}`}
                  >
                    {col.label}
                    {sort.field === col.key && (
                      <span className="ml-0.5 text-content-action dark:text-blueberry-400">{sort.dir === 'DESC' ? '↓' : '↑'}</span>
                    )}
                    {onColumnWidthsChange && (
                      // Sits ON the header's right border. `draggable={false}`
                      // + the dragstart/click stoppers keep the two header
                      // gestures (resize here, HTML5 reorder on the <th>)
                      // strictly separate — see `startColumnResize`.
                      <span
                        role="separator"
                        aria-orientation="vertical"
                        aria-label={`Resize ${col.label} column`}
                        data-resize-handle={col.key}
                        draggable={false}
                        onMouseDown={e => startColumnResize(col.key, e)}
                        onDragStart={e => { e.preventDefault(); e.stopPropagation(); }}
                        onClick={e => e.stopPropagation()}
                        // Sits fully INSIDE the cell's right edge rather than
                        // straddling the border: a resized <th> gets
                        // `overflow-hidden` (that's what makes the explicit
                        // width bind instead of the label forcing the column
                        // wider), and the <th> is this handle's containing
                        // block — so anything hanging outside would be
                        // clipped, leaving a half-width handle after the
                        // first drag.
                        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize select-none hover:bg-content-action/40 dark:hover:bg-blueberry-400/40"
                      />
                    )}
                  </th>
                );
              })}
              {config.rowAction && (
                // border-l closes the seam at the sticky column's OWN
                // floating position (D33a follow-up): the last regular
                // column's border-r only paints at its true in-flow spot,
                // which scrolls out from under this cell once the table is
                // scrolled — so without its own left border this header cell
                // has no visible edge and reads as blank chrome floating
                // over whatever column is now underneath it, while the body
                // cell below (which DOES need this same border, see :1062)
                // renders content there. Matching border-l on both keeps the
                // header/body pair reading as one demarcated column at any
                // scroll offset.
                <th className={`sticky right-0 w-20 bg-bg-secondary dark:bg-nightshade-800 ${
                  cardMode
                    ? cardCellEdges(cardColumns, cardColumns.length - 1, { top: true, roundTop: true })
                    : 'border-b border-l border-secondary dark:border-nightshade-700'
                }`} />
              )}
            </tr>
          </thead>
          <tbody>
            {isLoading && spanRow('Loading…', 'px-3 py-8 text-center text-content-tertiary dark:text-nightshade-500 text-[12px]')}
            {error && !isLoading && spanRow(error, 'px-3 py-8 text-left text-content-danger dark:text-red-400 text-[12px]')}
            {!isLoading && !error && rows.length === 0 && spanRow(
              emptyState ?? 'No results.',
              'px-3 py-8 text-center text-content-tertiary dark:text-nightshade-500 text-[12px]',
            )}
            {!isLoading && !error && rows.map((row, i) => {
              const id = config.rowKey(row);
              const isExpanded = expandedId === id;
              // Zebra stripe on odd data rows when the toggle is on. bg-canvas
              // (#FAFAF9) is opaque in light mode (no bleed behind the sticky
              // action cell); dark uses a faint translucent tint. Applied to
              // BOTH the <tr> and the sticky action cell so the stripe reads
              // across the full row width.
              const stripe = zebra && i % 2 === 1;
              // Mode-tuned stripe (Spencer, 2026-07-31): a soft light-gray in
              // light mode; a shade DARKER than the nightshade-900 card in dark
              // mode. Both solid (opaque) so nothing bleeds behind the sticky
              // action column on horizontal scroll. The sticky action cell
              // also carries its own border-l (D33a follow-up, see :1062) so
              // the opaque backdrop reads as a demarcated column edge, not
              // just an untethered patch of color.
              const stripeBg = 'bg-nightshade-50 dark:bg-nightshade-950';
              // `config.rowClassName` (U14 / A10) — a row-level highlight
              // that is NOT selection. It REPLACES the zebra stripe rather
              // than stacking with it: two competing `bg-*` utilities on one
              // element resolve by CSS source order, not by the order they
              // appear in the class string, so stacking them would make the
              // winner depend on Tailwind's output ordering. Replacing is
              // deterministic, and a highlighted row wanting the stripe back
              // is not a case that exists.
              const rowClass = config.rowClassName?.(row);
              // `cardMode`'s fallback (QA visual follow-up): every data row
              // needs an OPAQUE background of its own once the outer card's
              // shared `bg-bg-raised`/`bg-nightshade-900` is gone (floating
              // cards) — otherwise the page background shows through between
              // rows, not just in the 12px inter-block gap.
              const rowBg = rowClass || (stripe ? stripeBg : (cardMode ? 'bg-white dark:bg-nightshade-900' : ''));
              // The row's full value tuple, one entry per grouping level —
              // `[]` when ungrouped. A header renders at level `k` when this
              // is the first row or ANY level 0..k differs from the previous
              // row's (bead spec: "header when i === 0 or any of
              // path[0..k] differs from the previous row's") — a shallower
              // level changing always starts a fresh group at every deeper
              // level too, even if a deeper value happens to repeat.
              const groupPath = groupLevels.map(k => groupValueFor(row, k));
              const prevGroupPath = i > 0 ? groupLevels.map(k => groupValueFor(rows[i - 1], k)) : null;
              const boundaryAtLevel = (k: number): boolean => {
                if (i === 0 || prevGroupPath === null) return true;
                for (let j = 0; j <= k; j++) {
                  if (groupPath[j] !== prevGroupPath[j]) return true;
                }
                return false;
              };
              // Every row of a group shares the same path prefix, so this
              // reads the SAME collapsed state whether or not this
              // particular row is the one carrying that level's header.
              // Rows are skipped below when any level (own or ancestor) is
              // collapsed; a header itself is skipped only when an ANCESTOR
              // (not itself) is collapsed — collapsing a level hides its
              // rows AND its descendant headers, but the level's own header
              // stays visible (it's the toggle that brings it back). No
              // virtualization exists and the server ORDER BY prefix +
              // keyset cursor guarantee group contiguity, so a group can
              // never reopen on a later page.
              const rowHiddenByGroup = groupLevels.length > 0 && ancestorCollapsed(groupPath, groupLevels.length);
              // Floating-card bottom edge (`cardMode` only): true when THIS
              // row is the last rendered element of its top-level block (see
              // `blockBoundaries` above). `effectiveRowIsBlockBottom` defers
              // the bottom cap to the expanded detail panel instead when one
              // is open directly below this row — the detail `<tr>` renders
              // AFTER this one and is the thing that actually ends up last in
              // the DOM, so IT needs the rounded bottom corners, not this row
              // (a card would otherwise show a squared-off detail panel
              // hanging off a rounded corner one row up).
              const rowIsBlockBottom = cardMode && !!blockBoundaries?.bottomRowIds.has(String(id));
              const detailWillFollow = isExpanded && !!config.renderDetail;
              const effectiveRowIsBlockBottom = rowIsBlockBottom && !detailWillFollow;
              // Card mode per-cell grid lines + background (see `cardCellEdges`
              // and the <tr> className note below). Same background rule the
              // sticky action cell already used in both modes, so the whole
              // row hovers/expands as one band.
              const cardEdges = (idx: number) => cardCellEdges(cardColumns, idx, { roundBottom: effectiveRowIsBlockBottom });
              const cardBg = isExpanded
                ? 'bg-bg-secondary dark:bg-nightshade-800'
                : `${rowBg} group-hover:bg-bg-secondary dark:group-hover:bg-nightshade-800`;
              return (
                <React.Fragment key={id}>
                  {groupLevels.map((_key, k) => {
                    if (!boundaryAtLevel(k) || ancestorCollapsed(groupPath, k)) return null;
                    const value = groupPath[k];
                    const pathKey = groupPath.slice(0, k + 1).join(GROUP_PATH_SEP);
                    const count = groupCounts?.get(pathKey) ?? 0;
                    const levelCollapsed = isGroupCollapsed(pathKey, k, value);
                    return (
                      <React.Fragment key={pathKey}>
                      {k === 0 && i > 0 && sectionHeaders && (
                        <tr key={`group-gap-${pathKey}`} data-testid="group-gap" aria-hidden="true">
                          <td colSpan={totalCols} className="h-3 p-0 border-0" />
                        </tr>
                      )}
                      {cardMode ? (() => {
                        // Card mode (visual QA round 2): one cell per column so the
                        // column dividers run unbroken through group headers
                        // (Airtable-style). The label sits in the first data
                        // column's cell; the rest are empty but keep their lines.
                        // The WHOLE row toggles (the <tr> click); the label cell
                        // keeps role="button"/aria-expanded/keyboard handling so
                        // it stays the accessible toggle. The first block's
                        // level-0 header joins the sticky column header (which
                        // owns the top line + rounded top corners) — so it draws
                        // no top line or rounding of its own, and no gap row
                        // renders above it.
                        const isBlockTop = k === 0 && i > 0;
                        const isBlockBottom = !!blockBoundaries?.bottomHeaderKeys.has(pathKey);
                        const edgeOpts = { top: isBlockTop, roundTop: isBlockTop, roundBottom: isBlockBottom };
                        const firstDataKey = cols[0]?.key;
                        return (
                          <tr
                            key={`group-header-${pathKey}`}
                            onClick={() => toggleGroupCollapsed(pathKey, k, value)}
                            className="cursor-pointer select-none"
                          >
                            {cardColumns.map((kind, colIdx) => {
                              if (colIdx === cardLabelIdx) {
                                return (
                                  <td
                                    key={cardColumnKeys[colIdx]}
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={!levelCollapsed}
                                    onKeyDown={e => {
                                      if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        toggleGroupCollapsed(pathKey, k, value);
                                      }
                                    }}
                                    style={{ ...(firstDataKey ? widthStyleFor(firstDataKey) : undefined), paddingLeft: 10 + k * 12 }}
                                    className={`${GROUP_HEADER_SECTION_CLASS} max-w-[280px] overflow-hidden whitespace-nowrap ${cardCellEdges(cardColumns, colIdx, { ...edgeOpts, bottom: false })}`}
                                  >
                                    <span className="flex items-center gap-1.5 min-w-0">
                                      <span
                                        aria-hidden="true"
                                        className={`inline-block shrink-0 w-1.5 h-1.5 rounded-full ${GROUP_LEVEL_ACCENT_CLASSES[k] ?? GROUP_LEVEL_ACCENT_CLASSES[0]}`}
                                      />
                                      {levelCollapsed
                                        ? <ChevronRight className="w-3 h-3 shrink-0" />
                                        : <ChevronDown className="w-3 h-3 shrink-0" />}
                                      <span className="truncate min-w-0" title={value}>{value}</span>{' '}
                                      <span className="shrink-0 font-normal text-content-tertiary dark:text-nightshade-500">({count})</span>
                                    </span>
                                  </td>
                                );
                              }
                              return (
                                <td
                                  key={cardColumnKeys[colIdx]}
                                  className={`h-12 p-0 bg-bg-secondary dark:bg-nightshade-800 ${kind === 'action' ? 'sticky right-0 ' : ''}${cardCellEdges(cardColumns, colIdx, edgeOpts)}`}
                                />
                              );
                            })}
                          </tr>
                        );
                      })() : (
                      <tr key={`group-header-${pathKey}`}>
                        <td
                          colSpan={totalCols}
                          role="button"
                          tabIndex={0}
                          aria-expanded={!levelCollapsed}
                          onClick={() => toggleGroupCollapsed(pathKey, k, value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleGroupCollapsed(pathKey, k, value);
                            }
                          }}
                          style={{ paddingLeft: 10 + k * 12 }}
                          className={sectionHeaders ? GROUP_HEADER_SECTION_CLASS : GROUP_HEADER_COMPACT_CLASS}
                        >
                          {/* The accent dot is a MULTI-level-only affordance
                              (`groupLevels.length > 1`) — a single grouping
                              level (Contacts/Accounts/Plays' common compact
                              case, e.g. `PlaysSubtab.tsx`'s one-element
                              `groupBy`) renders byte-identical to the
                              pre-existing pre-dot markup: no dot, `gap-1`.
                              Signals never reaches this branch at all (its
                              `groupHeaderStyle: 'section'` always takes the
                              card-mode branch above, dot included there
                              unconditionally). */}
                          {groupLevels.length > 1 ? (
                            <span className="inline-flex items-center gap-1.5">
                              <span
                                aria-hidden="true"
                                className={`inline-block w-1.5 h-1.5 rounded-full ${GROUP_LEVEL_ACCENT_CLASSES[k] ?? GROUP_LEVEL_ACCENT_CLASSES[0]}`}
                              />
                              {levelCollapsed
                                ? <ChevronRight className="w-3 h-3" />
                                : <ChevronDown className="w-3 h-3" />}
                              {value} <span className="font-normal text-content-tertiary dark:text-nightshade-500">({count})</span>
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1">
                              {levelCollapsed
                                ? <ChevronRight className="w-3 h-3" />
                                : <ChevronDown className="w-3 h-3" />}
                              {value} <span className="font-normal text-content-tertiary dark:text-nightshade-500">({count})</span>
                            </span>
                          )}
                        </td>
                      </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                  {!rowHiddenByGroup && (
                  <>
                  <tr
                    ref={initialExpandedId && id === initialExpandedId ? initialRowRef : undefined}
                    // Generic row identity hook for external DOM-targeting
                    // consumers (e.g. ContactFlowTable's "View on contact →"
                    // jump — flow-workflow-requests ITEM 4): scroll-into-view
                    // + flash-highlight a row from OUTSIDE this table's own
                    // render tree, without threading a new prop through every
                    // FlowTable mount.
                    data-row-id={String(id)}
                    onClick={e => {
                      // Cmd/Ctrl/Shift-click operate selection ONLY (R2) —
                      // preventDefault + return BEFORE the renderDetail/
                      // onRowClick branch below, which a plain click still
                      // reaches completely untouched.
                      if (gestures.onRowClick(e, id)) return;
                      if (config.renderDetail) setExpandedId(isExpanded ? null : id);
                      else config.onRowClick?.(row);
                    }}
                    onMouseDown={gestures.onRowMouseDown}
                    // Card mode: the <tr> carries neither the divider nor the
                    // background — under border-separate a row border never
                    // paints, and a row background would fill the square
                    // corners that rounded cells cut away. Each cell owns both
                    // instead (`cardEdges` / `cardBg`).
                    className={cardMode
                      ? `group h-[42px] ${clickable ? 'cursor-pointer' : ''}`
                      : `group h-[42px] border-b border-secondary dark:border-nightshade-700 ${clickable ? 'cursor-pointer' : ''} ${isExpanded ? 'bg-bg-secondary dark:bg-nightshade-800' : `${rowBg ? `${rowBg} ` : ''}hover:bg-bg-secondary/60 dark:hover:bg-nightshade-800/60`}`}
                  >
                    {selection && (
                      <td
                        onClick={e => e.stopPropagation()}
                        onMouseDown={gestures.onRowMouseDown}
                        className={cardMode ? `px-2.5 ${cardBg} ${cardEdges(0)}` : 'px-2.5 border-r border-secondary dark:border-nightshade-700'}
                      >
                        <Checkbox
                          aria-label={selection.ariaLabelFor ? selection.ariaLabelFor(row) : 'Select row'}
                          checked={selection.selectedIds.has(id)}
                          onCheckedChange={(_, details) => gestures.onCheck(id, isShiftFromCheckboxEvent(details))}
                        />
                      </td>
                    )}
                    <td className={cardMode
                      ? `pl-2 text-content-tertiary dark:text-nightshade-500 ${cardBg} ${cardEdges(cardColumns.indexOf('chevron'))}`
                      : 'pl-2 text-content-tertiary dark:text-nightshade-500'}>
                      {config.renderDetail ? (
                        isExpanded
                          ? <ChevronDown className="w-3.5 h-3.5" />
                          : <ChevronRight className="w-3.5 h-3.5" />
                      ) : null /* Navigate rows: row hover bg + cursor-pointer are the affordances (chevrons mean "expand"). */}
                    </td>
                    {config.tracker && (
                      <td
                        onClick={e => e.stopPropagation()}
                        className={cardMode
                          ? `px-1.5 py-1 ${cardBg} ${cardEdges(cardColumns.indexOf('tracker'))}`
                          : 'px-1.5 py-1 border-r border-secondary dark:border-nightshade-700'}
                      >
                        <TrackerChip
                          entity={config.tracker.entity}
                          recordId={id}
                          status={trackerStatusOf(row.tracker_status)}
                          date={stringFieldOf(row.tracker_date)}
                          note={stringFieldOf(row.tracker_note)}
                          bucket={stringFieldOf(row.tracker)}
                          recordLabel={stringFieldOf(row.name) ?? undefined}
                        />
                      </td>
                    )}
                    {cols.map((col, colIdx) => (
                      <td
                        key={col.key}
                        data-col-key={col.key}
                        // U10: an explicit width overrides the `max-w-[280px]`
                        // cap inline (inline beats the class), so a resized
                        // column truncates at the rep's width instead. The
                        // classes are unchanged, so an UNRESIZED column
                        // renders byte-for-byte as before.
                        style={widthStyleFor(col.key)}
                        className={cardMode
                          ? `px-2.5 py-1 whitespace-nowrap max-w-[280px] truncate ${cardEdges(cardLabelIdx + colIdx)} ${cardBg} text-content-primary dark:text-nightshade-100 ${col.numeric ? 'text-right tabular-nums' : ''}`
                          : `px-2.5 py-1 whitespace-nowrap max-w-[280px] truncate border-r border-secondary dark:border-nightshade-700 text-content-primary dark:text-nightshade-100 ${col.numeric ? 'text-right tabular-nums' : ''}`}
                      >
                        {col.render ? col.render(row) : formatDefault(row[col.key])}
                      </td>
                    ))}
                    {config.rowAction && (
                      // border-l is unconditional (outside the isExpanded
                      // ternary) — same seam as the header <th> at :899, so
                      // the two always agree regardless of row state
                      // (expanded / striped / hovered). Without it this cell
                      // was opaque but edgeless, so it read as a floating
                      // overlay rather than a column with its own boundary
                      // once horizontal scroll put a different column
                      // underneath it (D33a follow-up).
                      <td
                        onClick={e => e.stopPropagation()}
                        className={`sticky right-0 px-2 py-1 text-right whitespace-nowrap ${
                          cardMode ? cardEdges(cardColumns.length - 1) : 'border-l border-secondary dark:border-nightshade-700'
                        } ${
                          isExpanded
                            ? 'bg-bg-secondary dark:bg-nightshade-800'
                            : `${rowClass || (stripe ? stripeBg : 'bg-white dark:bg-nightshade-900')} group-hover:bg-bg-secondary dark:group-hover:bg-nightshade-800`
                        }`}
                      >
                        {config.rowAction(row)}
                      </td>
                    )}
                  </tr>
                  {isExpanded && config.renderDetail && (
                    <tr>
                      <td
                        colSpan={totalCols}
                        className={cardMode ? 'p-0' : 'bg-bg-secondary/50 dark:bg-nightshade-800/50 border-b border-secondary dark:border-nightshade-700 p-0'}
                      >
                        {/* sticky-left keeps the detail cards in view while the table scrolls
                            horizontally; width = the measured visible container width. In card
                            mode the card's own edges (bg, border-b/l/r, rounded-b-lg) ride THIS
                            div rather than the colSpan <td> above: the <td> spans the table's
                            TRUE width, which extends past the visible viewport once the table
                            scrolls horizontally, so a border painted there lands off-screen. This
                            div is already sized to containerWidth and pinned at left:0, so its
                            edges land at the viewport's right edge — the same place the pinned
                            action-column cell puts them in every other row (fix-plan batch 5, D3). */}
                        <div
                          className={`sticky left-0${cardMode ? ` bg-bg-secondary/50 dark:bg-nightshade-800/50 border-b border-l border-r ${CARD_BORDER_COLOR}${rowIsBlockBottom ? ' rounded-b-lg' : ''}` : ''}`}
                          style={containerWidth ? { width: containerWidth } : undefined}
                        >
                          {config.renderDetail(row)}
                        </div>
                      </td>
                    </tr>
                  )}
                  </>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        {hasMore && !isLoading && (
          <div className="p-2 text-center border-t border-secondary dark:border-nightshade-700">
            <button
              onClick={onLoadMore}
              disabled={isLoadingMore}
              className="px-3 py-1 text-[12px] border border-secondary dark:border-nightshade-700 rounded-md text-content-secondary dark:text-nightshade-400 bg-white dark:bg-nightshade-800 hover:bg-bg-secondary dark:hover:bg-nightshade-700 disabled:opacity-50"
            >
              {isLoadingMore ? 'Loading…' : (loadMoreLabel ?? 'Load more')}
            </button>
          </div>
        )}
      </div>
    </div>
    </FilterDraftProvider>
  );
}

/**
 * Row is generic (`Record<string, unknown>`), so the tracker fields the chip
 * column reads off each row (`tracker_status`/`tracker_date`/`tracker_note`/
 * `tracker`) must be narrowed defensively rather than typed.
 */
function trackerStatusOf(v: unknown): TrackerStatus | null {
  const parsed = TrackerStatusSchema.safeParse(v);
  return parsed.success ? parsed.data : null;
}
function stringFieldOf(v: unknown): string | null {
  return typeof v === 'string' ? v : null;
}

function formatDefault(v: unknown): React.ReactNode {
  const out = formatCellValue(v);
  return out ?? <span className="text-content-faint dark:text-nightshade-600">—</span>;
}
